import {
  type BridgeTokenTransfer,
  BridgeTokenTransferStatus,
  DefaultDataProvider,
  EthersEtherlinkBridgeBlockchainService,
  type NativeEtherlinkToken,
  TaquitoWalletTezosBridgeBlockchainService,
  TokenBridge,
  type TokenPair,
} from "@baking-bad/tezos-etherlink-bridge-sdk";
import { type MichelsonTypeNat, packDataBytes } from "@taquito/michel-codec";
import { type TezosToolkit } from "@taquito/taquito";
import { type Signer, ethers, parseUnits } from "ethers";

import { type BridgeNetwork, config } from "./config";

const TOKEN_PAIRS_SHADOWNET: TokenPair[] = [
  {
    tezos: {
      type: "native",
      ticketHelperContractAddress: "KT1VEjeQfDBSfpDH5WeBM5LukHPGM2htYEh3",
    },
    etherlink: {
      type: "native",
    },
  },
];

const TOKEN_PAIRS_MAINNET: TokenPair[] = [
  {
    tezos: {
      type: "native",
      ticketHelperContractAddress: "KT1Wj8SUGmnEPFqyahHAcjcNQwe6YGhEXJb5",
    },
    etherlink: {
      type: "native",
    },
  },
];

const FAST_WITHDRAW_PRECOMPILE_ADDRESS = "0xff00000000000000000000000000000000000001";
const FAST_WITHDRAWAL_CONTRACT_ADDRESS = "KT1BGwyCrnJ6HuEYP7X8Q2UooTdxmEYHiK6j";
const FAST_WITHDRAW_ABI = [
  {
    type: "function",
    name: "fast_withdraw_base58",
    constant: false,
    payable: true,
    inputs: [
      { type: "string", name: "target" },
      { type: "string", name: "fast_withdrawal_contract" },
      { type: "bytes", name: "payload" },
    ],
  },
];

export class TezosBridge {
  private tokenBridge: TokenBridge | null = null;
  private network: BridgeNetwork;
  private toolkit: TezosToolkit | null = null;
  private signer: Signer | null = null;

  constructor({ network = "shadownet" }: { network?: BridgeNetwork } = {}) {
    this.network = network;
  }

  /**
   * Initialize the bridge with the required toolkit and signer
   */
  initialize(toolkit: TezosToolkit, signer: Signer) {
    try {
      this.toolkit = toolkit;
      this.signer = signer;
      const cfg = config[this.network];
      const defaultDataProvider = new DefaultDataProvider({
        tokenPairs: this.network === "shadownet" ? TOKEN_PAIRS_SHADOWNET : TOKEN_PAIRS_MAINNET,
        dipDup: {
          baseUrl: cfg.dipDupBaseUrl,
          webSocketApiBaseUrl: cfg.dipDupWebSocketUrl,
        },
        tzKTApiBaseUrl: cfg.tzktApiBaseUrl,
        etherlinkRpcUrl: cfg.etherlinkRpcUrl,
      });

      this.tokenBridge = new TokenBridge({
        tezosBridgeBlockchainService: new TaquitoWalletTezosBridgeBlockchainService({
          tezosToolkit: this.toolkit,
          smartRollupAddress: cfg.tezosSmartRollupAddress,
        }),
        etherlinkBridgeBlockchainService: new EthersEtherlinkBridgeBlockchainService({
          ethers,
          signer: this.signer,
        }),
        bridgeDataProviders: {
          transfers: defaultDataProvider,
          balances: defaultDataProvider,
          tokens: defaultDataProvider,
        },
      });

      return this;
    } catch (error) {
      console.error("Failed to initialize TezosBridge:", error);
      throw error;
    }
  }

  /**
   * Deposits XTZ from Tezos to Etherlink
   */
  async depositXtz(amount: bigint) {
    if (!this.tokenBridge || !this.toolkit || !this.signer) {
      throw new Error(
        "TezosBridge not initialized. Call initialize() first with toolkit and signer."
      );
    }

    const xtzToken = {
      type: "native",
    } as const;

    const { tokenTransfer, operationResult } = await this.tokenBridge.deposit(amount, xtzToken);

    const finishedTransfer = await this.tokenBridge.waitForStatus(
      tokenTransfer,
      BridgeTokenTransferStatus.Finished
    );

    return { tokenTransfer: finishedTransfer, operationResult };
  }

  /**
   * Fast withdrawal: Withdraws XTZ from Etherlink to Tezos using the fast withdrawal contract
   * @param sellAmount - amount in wei (18 decimals) sent from Etherlink
   * @param buyAmount - amount in XTZ (6 decimals, as string) to receive on Tezos (after fee)
   * @param tezosAddress - destination Tezos address (base58)
   * @returns ethers.js TransactionResponse
   */
  async fastWithdrawXtz(sellAmount: bigint, buyAmount: string, tezosAddress: string) {
    if (!this.signer) {
      throw new Error(
        "TezosBridge not initialized. Call initialize() first with toolkit and signer."
      );
    }
    const amountInMutez = parseUnits(buyAmount, 6);
    const amountInMutezNat = { int: amountInMutez.toString() };
    const natType: MichelsonTypeNat = { prim: "nat" };
    const packedPayload = packDataBytes(amountInMutezNat, natType);
    const contract = new ethers.Contract(
      FAST_WITHDRAW_PRECOMPILE_ADDRESS,
      FAST_WITHDRAW_ABI,
      this.signer
    );
    const provider = this.signer.provider;
    const feeData = provider && (await provider.getFeeData());
    const baseFee = feeData?.maxFeePerGas || feeData?.gasPrice;
    const tx = await contract.fast_withdraw_base58(
      tezosAddress,
      FAST_WITHDRAWAL_CONTRACT_ADDRESS,
      "0x" + packedPayload.bytes,
      {
        value: sellAmount,
        maxPriorityFeePerGas: parseUnits("1", "gwei"),
        maxFeePerGas: baseFee,
      }
    );

    return tx;
  }

  /**
   * Withdraws XTZ from Etherlink to Tezos
   * @param amount - amount in wei (18 decimals)
   * @param opts - { fastWithdrawal?: boolean, tezosAddress?: string, buyAmount?: string }
   */
  async withdrawXtz(
    amount: bigint,
    opts?: { fastWithdrawal?: boolean; tezosAddress?: string; buyAmount?: string }
  ) {
    if (opts?.fastWithdrawal) {
      if (!opts.tezosAddress || !opts.buyAmount) {
        throw new Error("tezosAddress and buyAmount are required for fast withdrawal");
      }

      return this.fastWithdrawXtz(amount, opts.buyAmount, opts.tezosAddress);
    }

    if (!this.tokenBridge || !this.toolkit || !this.signer) {
      throw new Error(
        "TezosBridge not initialized. Call initialize() first with toolkit and signer."
      );
    }
    const tokenBridge = this.tokenBridge;
    const xtzToken: NativeEtherlinkToken = {
      type: "native",
    };
    const { operationResult } = await tokenBridge.startWithdraw(amount, xtzToken);
    const statusPromise = new Promise<BridgeTokenTransfer>((resolve, reject) => {
      const handleTokenTransferUpdated = (transfer: BridgeTokenTransfer) => {
        if (transfer.status === BridgeTokenTransferStatus.Failed) {
          reject(new Error("Token transfer failed"));
        } else if (transfer.status === BridgeTokenTransferStatus.Created) {
          resolve(transfer);
        }
      };
      tokenBridge.addEventListener("tokenTransferUpdated", handleTokenTransferUpdated);
    });
    tokenBridge.stream.subscribeToTokenTransfers();
    const createdTransfer = await statusPromise;

    return {
      tokenTransfer: createdTransfer,
      operationResult,
    };
  }
}
